import { useEffect, useRef, useState } from 'react'
import { useControllableValue, useEscapeKey, useOnClickOutside } from '@/ui/kit'

const HOVER_CLOSE_DELAY = 120

/**
 * Shared open/close behaviour for Tooltip, Popover, Popconfirm and Dropdown.
 * antd's `trigger` is a string or an array of 'hover' | 'click' | 'focus'.
 *
 * @param {object} config
 * @param {string|string[]} [config.trigger='hover']
 * @param {boolean} [config.open] controlled visibility
 * @param {boolean} [config.defaultOpen]
 * @param {(open: boolean) => void} [config.onOpenChange]
 * @param {number} [config.mouseEnterDelay] seconds, as antd expresses it
 * @param {boolean} [config.disabled]
 */
export const useTrigger = ({
  trigger = 'hover',
  open,
  defaultOpen = false,
  onOpenChange,
  mouseEnterDelay = 0.1,
  mouseLeaveDelay = 0.1,
  disabled = false,
  closeOnOutside = true,
} = {}) => {
  const triggers = Array.isArray(trigger) ? trigger : [trigger]
  const [isOpen, setOpen] = useControllableValue(open, defaultOpen, onOpenChange)
  const anchorRef = useRef(null)
  const popupRef = useRef(null)
  const timerRef = useRef(null)
  const [, forceRender] = useState(0)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const schedule = (next, seconds) => {
    clearTimeout(timerRef.current)
    const delay = Math.max(0, (seconds ?? 0) * 1000)
    if (delay === 0) {
      setOpen(next)
      return
    }
    timerRef.current = setTimeout(() => setOpen(next), delay)
  }

  const close = () => {
    clearTimeout(timerRef.current)
    setOpen(false)
  }

  useOnClickOutside(
    [anchorRef, popupRef],
    close,
    isOpen && closeOnOutside && triggers.includes('click')
  )
  useEscapeKey(close, isOpen)

  const anchorProps = disabled
    ? {}
    : {
        ...(triggers.includes('click')
          ? {
              onClick: (event) => {
                event.stopPropagation()
                setOpen(!isOpen)
              },
            }
          : {}),
        ...(triggers.includes('hover')
          ? {
              onMouseEnter: () => schedule(true, mouseEnterDelay),
              onMouseLeave: () => schedule(false, mouseLeaveDelay),
            }
          : {}),
        ...(triggers.includes('focus')
          ? {
              onFocus: () => setOpen(true),
              onBlur: () => setOpen(false),
            }
          : {}),
      }

  // Hovering the popup itself must keep it open (antd behaviour for Popover menus).
  const popupHoverProps = triggers.includes('hover')
    ? {
        onMouseEnter: () => clearTimeout(timerRef.current),
        onMouseLeave: () => schedule(false, mouseLeaveDelay || HOVER_CLOSE_DELAY / 1000),
      }
    : {}

  return {
    isOpen: Boolean(isOpen) && !disabled,
    setOpen,
    close,
    anchorRef,
    popupRef,
    anchorProps,
    popupHoverProps,
    forceRender,
  }
}

export default useTrigger
